/**
 * Module 'scheduler': where missions run (product gap analysis sections 6.7 and 7.1).
 *
 * Missions queue when no eligible machine has room, and start by themselves when one frees up.
 * Placement reads machine labels (the scheduling language: `needs: [linux, gpu]`), the runtime,
 * time rules ("no new work on laptop from 22:00 to 07:00"), drained machines and limits per
 * project and per person. Room on a machine (ADR 0063 step 5, admission) is heavy-work headroom
 * and memory, not session count: with a fresh machine-load report a machine takes a session
 * while its work budget has free slots or a short queue, its CPU over the report's window is
 * under the ceiling, and its free memory stays over the floor after one more session. Session
 * count is only a sanity cap derived from memory. Without a report (an older agent, a stale one)
 * the earlier rules apply: CPU and memory headroom and CPU count / 2 sessions. Node pools are
 * templates a provider turns into machines; the pool planner scales them up when missions wait
 * and down when their machines sit idle.
 *
 * `schedule`, `roomFor` and `planPools` are pure and browser-safe: the hub runs them on a timer,
 * the console and the assistant run the same functions to explain, live, why a mission waits and
 * where a session has room.
 */
import type { Id, Machine, Ts } from '../index.ts';
import type { MachineLoadReport } from './machine-load.ts';
import { ACP_BASE_CAPABILITIES, acpCapabilities, agentCapabilities } from './adapters.ts';
import type { AgentSupport } from './adapters.ts';
import { acpAgent } from './agent-kinds.ts';
import { exploreDraw, scoreAgents, spareOrder } from './agent-score.ts';
import type { AgentOutcome, AgentPick, WorkSize } from './agent-score.ts';
import type { AgentFailover, AgentHealth, CoolingMap } from './agent-health.ts';
import type { AgentBreaker, BrokenMap, SelfTestResult } from './agent-breaker.ts';
import type { AgentUsage } from './agent-usage.ts';
import type { AgentEnvironmentReport } from './agent-environment.ts';
import type { FailureCause } from './failures.ts';
import { DEFAULT_AGENT_KIND, agentKindLabel } from './adapters.ts';
import { acceptsPermissionMode } from './agent-kinds.ts';

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/** What a queued mission pinned to an offline machine does: keep waiting for it, or run elsewhere. */
export type OfflinePolicy = 'wait' | 'reschedule';
export const OFFLINE_POLICY_LABEL: Record<OfflinePolicy, string> = {
  wait: 'Wait for the machine',
  reschedule: 'Run on another machine',
};

/** Machines carrying `label` take no new work from `from` until `to` (hub local time unless an offset is set). */
export interface TimeRule {
  id: Id;
  label: string;
  /** 'HH:MM'. */
  from: string;
  /** 'HH:MM' or '24:00'. A window with to <= from runs past midnight. Equal times block the whole day. */
  to: string;
  /** Days the window starts on, 0 Sunday to 6 Saturday. Empty: every day. */
  days: number[];
  note: string;
  createdBy: string;
  createdAt: Ts;
}

/** Per machine, set in Orgus (the agent never overwrites these). */
export interface NodeSettings {
  machineId: Id;
  /** Labels set in Orgus. They replace what the agent reports; null keeps the agent's. */
  labels: string[] | null;
  /**
   * Sessions at once, an explicit owner override. null: derived from memory while the machine
   * reports its load (`sessionCap`), else CPU count / 2 (at least 1).
   */
  maxSessions: number | null;
  /** Drained: no new work; running work finishes. */
  drain: { at: Ts; by: string; reason: string } | null;
}

export interface ProjectSchedulerSettings {
  offline: OfflinePolicy;
  /** Sessions at once across every machine; null: the default project limit. */
  maxSessions: number | null;
}

export interface SchedulerSettings {
  limits: {
    /** Default sessions at once per project; null: no limit beyond the machines'. */
    perProject: number | null;
    /** Sessions at once per person (mission owner); null: no limit beyond the machines'. */
    perPerson: number | null;
  };
  headroom: {
    /** A machine busier than this (0..1) takes no new work. */
    maxLoad: number;
    /** A machine with less free memory than this fraction takes no new work. */
    minFreeMemory: number;
  };
  defaultOffline: OfflinePolicy;
  projects: Record<Id, ProjectSchedulerSettings>;
  rules: TimeRule[];
  /** Minutes east of UTC for time rules; null: the hub's own time zone. */
  utcOffsetMinutes: number | null;
  /**
   * Agent kinds a mission tries first, in order, when nobody picked one (ADR 0049 decision 5);
   * the rest follow in the order the machine reports them. Absent or empty: the machine's order.
   * Additive, 19 Oct 2026.
   *
   * `off` (20 Oct 2026): agent kinds the owner turned off. Orgus starts none of them: placement
   * and failover skip them, a mission pinned to one waits and says so, and the hub refuses a
   * `session.start` that names one. Absent or empty: every agent is on.
   *
   * `spare` (20 Oct 2026): agent kinds whose credits the owner saves. 'auto' work goes to the
   * other kinds first and to a saved kind only for large work (5 points or more) or when no other
   * kind can take it (agent-score.ts sparedFor). Absent or empty: nothing is saved.
   */
  agents?: { preferred: string[]; off?: string[]; spare?: string[] };
  /**
   * ADR 0089: a runtime fault opens a breaker on its (machine, agent kind, setup), which only a
   * self-test closes (agent-breaker.ts). Only true turns it on while it lands dark; otherwise a
   * runtime fault is only evidence. Additive, 21 Oct 2026.
   */
  runtimeBreaker?: boolean;
}

/** Does a runtime fault open a breaker on this hub (SchedulerSettings.runtimeBreaker)? */
export function runtimeBreakerOn(settings: Pick<SchedulerSettings, 'runtimeBreaker'> | null | undefined): boolean {
  return settings?.runtimeBreaker === true;
}

/** Is this agent kind turned off in the scheduling settings? */
export function agentKindOff(settings: Pick<SchedulerSettings, 'agents'> | null | undefined, kind: string): boolean {
  return !!settings?.agents?.off?.includes(kind);
}

/** Why a session of this kind is not started, when the owner turned it off. */
export function agentOffMessage(kind: string): string {
  return `${agentKindLabel(kind)} is turned off for this hub. Turn it on in Capacity, Rules, or pick another agent.`;
}

/**
 * The entry of a machine's ceiling (CeilingsReport.deny, what the machine reports to module
 * 'trust') that refuses every session of this agent kind in this window, or null (20 Oct 2026):
 * '*', 'session.start', 'session.start:<mode>' or 'session.start:agent:<kind>'. Placement,
 * failover and every agent pick treat a refused kind like one that is off on that machine. An
 * entry that refuses only a permission mode is not the kind's: the start may pass in another mode.
 */
export function ceilingRefusesAgent(deny: readonly string[] | null | undefined, kind: string, mode: 'headless' | 'terminal' = 'headless'): string | null {
  if (!deny?.length) return null;
  for (const entry of ['*', 'session.start', `session.start:${mode}`, `session.start:agent:${kind}`]) if (deny.includes(entry)) return entry;
  return null;
}

/** Why a session of this kind does not start on a machine whose ceiling refuses it. */
export function agentRefusedMessage(kind: string, machine: string, entry: string): string {
  return `${agentKindLabel(kind)} is refused on ${machine} (its ceiling). Allow it there with orgus agent ceiling allow ${entry === '*' ? "'*'" : entry}, or pick another agent.`;
}

/** One agent kind a machine's ceiling refuses, for the console (SchedulerState.refused). */
export interface AgentRefusal {
  machineId: Id;
  kind: string;
  /** The ceiling entry that refuses it (ceilingRefusesAgent). */
  entry: string;
}

/**
 * Can this machine start Claude Code, which Orgus's own AI (the assistant, AI jobs) runs on? An
 * agent that reports no adapters (agentKinds absent) predates them and starts Claude Code.
 */
export function startsClaudeCode(m: Pick<Machine, 'agentKinds'>): boolean {
  return !m.agentKinds || (m.agentKinds as readonly string[]).includes(DEFAULT_AGENT_KIND);
}

export const DEFAULT_SCHEDULER_SETTINGS: SchedulerSettings = {
  limits: { perProject: null, perPerson: null },
  headroom: { maxLoad: 0.9, minFreeMemory: 0.08 },
  defaultOffline: 'wait',
  projects: {},
  rules: [],
  utcOffsetMinutes: null,
};

/** The agent kind a machine runs when it names none, and a manual start's default. */
export const DEFAULT_RUNTIME = 'claude-code';

/** `MissionNeeds.runtime` for "any working agent": placement picks the kind (ADR 0049 decision 5). */
export const AUTO_AGENT = 'auto';

/** An 'auto' mission moves to another agent at most this many times; then it fails with every reason (ADR 0049 decision 5). */
export const MAX_AGENT_SWITCHES = 2;

/**
 * A mission whose session's process ended with the agent that ran it (a hub or agent restart,
 * SessionFailure.interrupted) starts again at most this many times, on any agent including the
 * same one, without cooling it or counting a switch (ADR 0085).
 */
export const MAX_RESTART_RESUMES = 5;

/**
 * An entry moves onto a per-token fallback account of the same agent at most this many times
 * (ADR 0071 decision 9, amended 20 Oct 2026). Counted apart from MAX_AGENT_SWITCHES: the same
 * agent continues, so a later switch of agent is still allowed. At most 4 restarts in all.
 */
export const MAX_ACCOUNT_FALLBACKS = 2;

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

export interface MissionNeeds {
  /** Every one of these must be on the machine. */
  labels: string[];
  /** An agent kind (pinned: only that kind runs it), or AUTO_AGENT (placement picks). */
  runtime: string;
  /** Kinds an 'auto' entry must not use (they failed it for a reason of their own). Additive, 19 Oct 2026. */
  avoid?: string[];
  /** The kind an 'auto' entry tries first: the one that moved onto a fallback account keeps its agent. Additive, 20 Oct 2026. */
  prefer?: string;
  /** Free memory the session needs, in GB. */
  minFreeMemGb: number | null;
}

/** How the session starts once placed (MissionStartRequest without the machine and checkout). */
export interface QueuedStart {
  mode: 'headless' | 'terminal';
  model: string | null;
  permissionMode: string | null;
  newWorktree: { branch: string } | null;
  note: string | null;
  /**
   * The checkout a person chose (only with a pinned machine). Absent or null: the machine's main
   * checkout of the project, else its first worktree. Additive, 18 Oct 2026.
   */
  checkoutId?: Id | null;
  /**
   * The launch account its next start runs on (ADR 0071): set when the work moved onto a
   * per-token fallback account; absent or null, the project's default applies. Additive, 20 Oct 2026.
   */
  launchAccountId?: string | null;
}

export type QueueState = 'queued' | 'starting' | 'started' | 'failed' | 'cancelled';
export const QUEUE_STATE_LABEL: Record<QueueState, string> = {
  queued: 'Queued',
  starting: 'Starting',
  started: 'Started',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

/** Why a queued mission is not running yet. Ordered from "nowhere near" to "almost". */
export type WaitReason =
  | 'no-machine'
  | 'no-checkout'
  | 'runtime'
  | 'labels'
  | 'pinned-offline'
  | 'offline'
  | 'drained'
  | 'time-rule'
  | 'headroom'
  | 'node-full'
  /** Every machine that fits has heavy work queued in its work budget (ADR 0063). Additive, 18 Oct 2026. */
  | 'work-full'
  | 'project-limit'
  | 'person-limit'
  /** A project on the machine lands or deploys first (project protocol, spec section 6). Never for urgent (priority 0). */
  | 'project-busy'
  /** Missions are paused, or this one is (ADR 0103): it stays queued until a person resumes. Additive, 22 Oct 2026. */
  | 'paused'
  /** A hub restart is pending (ORGU-154): only urgent work (priority 0, a red-main fix) starts until it is done. Additive, 30 Oct 2026. */
  | 'restart';

export const WAIT_REASON_LABEL: Record<WaitReason, string> = {
  'no-machine': 'No machines',
  'no-checkout': 'No checkout',
  runtime: 'Runtime missing',
  labels: 'No matching labels',
  'pinned-offline': 'Machine offline',
  offline: 'Machines offline',
  drained: 'Drained',
  'time-rule': 'Outside hours',
  headroom: 'Machines busy',
  'node-full': 'Machines full',
  'work-full': 'Heavy work queued',
  'project-limit': 'Project limit',
  'person-limit': 'Person limit',
  'project-busy': 'Waits for a landing',
  paused: 'Paused',
  restart: 'Hub restart pending',
};

export interface QueueEntry {
  id: Id;
  missionId: Id;
  projectId: Id;
  /** Title of the mission when queued, for lists. */
  title: string;
  /** The mission owner: per-person limits count against them. */
  person: string;
  /** Who queued it. */
  by: string;
  /**
   * Who it runs for (ADR 0042): the person who queued it, or for a token the person who made it.
   * Placement and the start judge this person. Absent on entries queued before it existed: `by`.
   * Additive, 18 Oct 2026.
   */
  for?: string;
  needs: MissionNeeds;
  /** Run only here (unless the machine is offline and the project reschedules). */
  machineId: Id | null;
  start: QueuedStart;
  /** 0 urgent .. 3 low; first come first served within a priority. */
  priority: number;
  state: QueueState;
  enqueuedAt: Ts;
  updatedAt: Ts;
  attempts: number;
  /** While queued: why it waits (the last scheduler pass). */
  reason: WaitReason | null;
  /** While queued: one plain sentence; when failed or started: what happened. */
  message: string | null;
  /** Where it was placed. */
  placedOn: Id | null;
  checkoutId: Id | null;
  startedAt: Ts | null;
  /** The launch the mission recorded (its audit id) and its session, once known. */
  launchId: Id | null;
  sessionId: Id | null;
  /** The agent kind its last start used (placement's pick for 'auto'). Additive, 19 Oct 2026. */
  agentKind?: string | null;
  /** Why placement picked that kind (and model) for 'auto', in one line (agent-score.ts). Additive, 19 Oct 2026. */
  agentReason?: string | null;
  /** Times an 'auto' entry moved to another agent after one failed it (at most MAX_AGENT_SWITCHES). Additive, 19 Oct 2026. */
  switches?: number;
  /** Every agent failure it went through, oldest first (ADR 0049 decision 5). Additive, 19 Oct 2026. */
  failovers?: AgentFailover[];
  /** The account its last start ran on (ADR 0071), when the hub keeps accounts. Additive, 20 Oct 2026. */
  account?: { id: string; name: string } | null;
  /** Times it moved onto a per-token fallback account (at most MAX_ACCOUNT_FALLBACKS). Additive, 20 Oct 2026. */
  accountSwitches?: number;
  /** Its last start got no reply in time and was placed again once, before a timeout counts (QA-127). Additive, 31 Oct 2026. */
  timeoutRetried?: boolean;
  /** Set once the session of its last start ended and the scheduler read the outcome. Additive, 19 Oct 2026. */
  outcome?: 'ok' | 'agent-failed' | 'task-failed' | null;
  /**
   * Pools whose machines joined, had room, and still could not run it (no checkout of the
   * project, no runtime, labels): those pools do not grow for it again.
   */
  unfitPools?: Id[];
}

/** Claude Code permission modes a queued start may ask for. */
export const SESSION_PERMISSION_MODES = ['default', 'acceptEdits', 'plan', 'bypassPermissions'] as const;

/** POST /api/scheduler/queue */
export interface EnqueueRequest {
  missionId: Id;
  /** Pin to one machine. */
  machineId?: Id | null;
  needs?: { labels?: string[]; runtime?: string; minFreeMemGb?: number | null };
  mode?: 'headless' | 'terminal';
  model?: string | null;
  permissionMode?: string | null;
  newWorktree?: { branch: string } | null;
  note?: string | null;
  priority?: number;
  /** Only with machineId: run in this checkout of the project there. */
  checkoutId?: Id | null;
}

/**
 * A person's start, the API behind the New session dialog's choice (ADR 0063 step 5): `now`
 * starts at once wherever they chose, whatever the room; `room` queues it as a mission that the
 * scheduler starts when a machine has room, pinned to `machineId` or on any machine with a
 * checkout of the project.
 */
export type StartWhen = 'now' | 'room';
export const START_WHEN: readonly StartWhen[] = ['now', 'room'];

/** POST /api/scheduler/start */
export interface ManualStartRequest {
  projectId: Id;
  prompt: string;
  when: StartWhen;
  /** now: required (or its checkout's machine); room: pins it, null runs on any machine. */
  machineId?: Id | null;
  /** now: required; room: only with machineId. */
  checkoutId?: Id | null;
  mode?: 'headless' | 'terminal';
  model?: string | null;
  permissionMode?: string | null;
  newWorktree?: { branch: string } | null;
  /** now only: queued starts run Claude Code. */
  agentKind?: string;
  /** room: the mission's title; default the prompt's first line. */
  title?: string;
}

export interface ManualStartResponse {
  when: StartWhen;
  /** The chosen machine's admission when the request came in; null with no machine or no scheduler view of it. */
  admission: Admission | null;
  /** now: what the machine answered (ActionResponse). */
  action?: unknown;
  /** room: the queue entry (it may have started already) and the mission behind it. */
  entry?: QueueEntry;
  missionId?: Id;
}

/** A mission title from a prompt: its first line, at most 80 characters. */
export function titleFromPrompt(prompt: string): string {
  const line = prompt.trim().split(/\r?\n/)[0]!.trim().replace(/\s+/g, ' ');
  return line.length > 80 ? `${line.slice(0, 79).trimEnd()}…` : line || 'Session';
}

// ---------------------------------------------------------------------------
// Node pools
// ---------------------------------------------------------------------------

export interface NodePool {
  id: Id;
  name: string;
  /** Provider id: 'fake' is the only one built in. */
  provider: string;
  image: string;
  size: string;
  labels: string[];
  min: number;
  max: number;
  /** Minutes a machine from this pool may sit idle before it is removed (above min). */
  idleTimeoutMin: number;
  createdBy: string;
  createdAt: Ts;
  /** When its image, size or labels last changed: machines from before may differ from new ones. */
  changedAt?: Ts;
}

export type InstanceState = 'provisioning' | 'running' | 'stopping' | 'gone' | 'failed';
export const INSTANCE_STATE_LABEL: Record<InstanceState, string> = {
  provisioning: 'Provisioning',
  running: 'Running',
  stopping: 'Stopping',
  gone: 'Removed',
  failed: 'Failed',
};

export interface PoolInstance {
  id: Id;
  poolId: Id;
  provider: string;
  /** The provider's own id for it. */
  ref: string;
  name: string;
  state: InstanceState;
  /** The machine it enrolled as, once it joined. */
  machineId: Id | null;
  /** Slots it brings (from its size), counted before it joins. */
  slots: number;
  createdAt: Ts;
  updatedAt: Ts;
  /** Since when it has had no running work. */
  idleSince: Ts | null;
  message: string | null;
}

export interface ProviderSize {
  id: string;
  label: string;
  cpus: number;
  memGb: number;
}

export interface ProviderInfo {
  id: string;
  label: string;
  /** False for providers that only simulate machines. */
  real: boolean;
  sizes: ProviderSize[];
  images: string[];
}

export interface PoolInput {
  name: string;
  provider: string;
  image: string;
  size: string;
  labels?: string[];
  min?: number;
  max?: number;
  idleTimeoutMin?: number;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface NodeCapacity {
  machineId: Id;
  name: string;
  online: boolean;
  /** Effective labels: Orgus's when set, else the agent's, plus the OS. */
  labels: string[];
  /** Labels from Orgus (true) or the agent (false). */
  labelsSet: boolean;
  limit: number;
  /** Running sessions plus starts not reported yet. */
  used: number;
  /** Starts placed here whose sessions have not appeared yet. */
  reserved: number;
  load: number;
  memFreeFraction: number;
  drained: NodeSettings['drain'];
  /** The time rule blocking it right now, if any. */
  blockedBy: TimeRule | null;
  /** Can it take new work at all right now (online, not drained, no rule, headroom)? Room aside. */
  accepting: boolean;
  /** Why not, when it is not. */
  notAccepting: string | null;
  /** Who uses it: running work grouped by person and project. */
  usage: { person: string | null; projectId: Id | null; count: number }[];
  /** Whether it takes one more session now, and why (ADR 0063 step 5). Absent from older hubs. */
  admission?: Admission;
}

/** state.ext.scheduler */
export interface SchedulerState {
  settings: SchedulerSettings;
  nodes: Record<Id, NodeSettings>;
  queue: QueueEntry[];
  pools: NodePool[];
  instances: PoolInstance[];
  providers: ProviderInfo[];
  capacity: NodeCapacity[];
  /** (machine, agent kind) pairs that failed recently, cooling or with strikes (ADR 0049 decision 5). Additive, 19 Oct 2026. */
  health?: AgentHealth[];
  /** Agent kinds a machine offers that its own ceiling refuses (ceilingRefusesAgent): never placed there. Additive, 20 Oct 2026. */
  refused?: AgentRefusal[];
  /**
   * Each (machine, agent kind) pair's latest subscription usage (module 'agent-usage'): windows,
   * percent used, resets. Routing weighs it (agent-score.ts capacity). Additive, 20 Oct 2026.
   */
  usage?: AgentUsage[];
  /** Whether machines read Claude Code's usage with a /usage run every 15 minutes (module 'agent-usage'). */
  usageProbe?: boolean;
  /** Runtime breakers that are not closed (ADR 0089): open, testing or half-open. Additive, 21 Oct 2026. */
  breakers?: AgentBreaker[];
  /** The newest self-test per (machine, kind), passed or not (ADR 0089). */
  selfTests?: SelfTestResult[];
  /** Machines whose Orgus agent froze lately (LoopFreezeReport): the machine's fault, never an agent's. */
  frozen?: { machineId: Id; maxMs: number; count: number; at: Ts }[];
  /** Each machine's latest environment parity report (module 'agent-environment', ADR 0093). Additive, 20 Oct 2026. */
  environment?: AgentEnvironmentReport[];
  /** A hub restart is pending and admission is closed but for red-main fixes (ORGU-154). Additive, 30 Oct 2026. */
  restartPending?: RestartPending;
  at: Ts;
}

/** What other hub modules may ask: ctx.ext<SchedulerApi>('scheduler'). */
export interface SchedulerApi {
  labelsOf(machineId: Id): string[];
  isDrained(machineId: Id): boolean;
  /**
   * The missions module asks this when a mission's session failed: true when the scheduler moved
   * the mission to another agent (so it is not announced as failed). Additive, 19 Oct 2026.
   */
  failingOver?(missionId: Id): boolean;
  /**
   * The state of the mission's newest queue entry, or null when it has none. Autopilot asks it so
   * a mission whose start failed in the queue frees its slot (QA-87). Additive, 30 Oct 2026.
   */
  queueState?(missionId: Id): QueueState | null;
  /**
   * The agent kind a start nobody pinned runs with on this machine: the first preferred kind it
   * offers, else the first it offers (ADR 0049 decision 5). With `projectId`, only kinds that
   * project's policy allows, and kinds that can ask when its policy needs approvals. Null when it
   * offers none.
   */
  agentKindFor?(machineId: Id, opts?: { permissionMode?: string | null; projectId?: Id | null; avoid?: readonly string[] | null }): string | null;
  /**
   * The (machine, kind) pairs cooling down right now after a failure of their own (a usage limit,
   * signed out, a crash), soonest reset first; only `machineId`'s when given. Autopilot asks it to
   * say which agents are limited and when each resets (ADR 0049 decision 5). Additive, 20 Oct 2026.
   */
  coolingAgents?(machineId?: Id | null): AgentHealth[];
  /**
   * A start outside the queue (Orgus fixes it, ADR 0064) failed for the agent's own reason: the
   * pair cools down as a queued failure would, so the next pick skips it. Additive, 20 Oct 2026.
   */
  agentFailed?(machineId: Id, kind: string, cause: FailureCause, message: string): void;
  /**
   * Is this agent kind turned off for the hub (SchedulerSettings.agents.off)? Orgus's own AI asks
   * before it starts Claude Code. Additive, 20 Oct 2026.
   */
  agentOff?(kind: string): boolean;
  /**
   * Module 'agent-usage' heard a new usage reading (null: only its settings changed): a
   * 'rejected' one cools the pair until its reset (cause 'usage-limit'), an allowed one ends a
   * usage-limit cooling, and the state is published again. Additive, 20 Oct 2026.
   */
  usageChanged?(usage: AgentUsage | null): void;
  /** Module 'agent-environment' heard a machine's new environment report: the state is published again. Additive, 20 Oct 2026. */
  environmentChanged?(): void;
  /**
   * The agent kinds that can start on this machine now, best first: offered there, not turned
   * off, not cooling down, not signed out; the preferred order, and kinds the owner saves credits
   * on (`agents.spare`) last with `spare: true`. Orgus's own AI (AI jobs) picks from these.
   * Additive, 20 Oct 2026.
   */
  agentKindsFor?(machineId: Id): { kind: string; spare: boolean }[];
  /** Runtime breakers that are not closed (ADR 0089), only `machineId`'s when given. Additive, 21 Oct 2026. */
  breakers?(machineId?: Id | null): AgentBreaker[];
  /**
   * Orgus's own AI hit a failure of the agent's own (usage limit, signed out, not installed) on
   * this machine: the pair cools down exactly as after a mission's failure. Additive, 20 Oct 2026.
   */
  agentFailed?(machineId: Id, kind: string, cause: FailureCause, message: string): void;
}

/** The most preferred agent kinds a hub keeps. */
export const MAX_PREFERRED_AGENT_KINDS = 16;

/** Why a preferred agent list is not valid, or null. */
/** Why a list of agent kinds to turn off is refused, or null. */
export function offAgentsError(v: unknown): string | null {
  if (!Array.isArray(v)) return 'Agents turned off must be a list of agent kinds.';
  if (v.length > 64) return 'Name at most 64 agents to turn off.';
  if (v.some((k) => typeof k !== 'string' || !/^[a-z0-9._-]{1,40}$/.test(k))) return 'That agent kind is not valid.';
  if (new Set(v).size !== v.length) return 'Name each agent to turn off once.';
  return null;
}

/** Why a list of agent kinds whose credits are saved is refused, or null. */
export function spareAgentsError(v: unknown): string | null {
  if (!Array.isArray(v)) return 'Agents whose credits are saved must be a list of agent kinds.';
  if (v.length > 64) return 'Name at most 64 agents to save credits on.';
  if (v.some((k) => typeof k !== 'string' || !/^[a-z0-9._-]{1,40}$/.test(k))) return 'That agent kind is not valid.';
  if (new Set(v).size !== v.length) return 'Name each agent to save credits on once.';
  return null;
}

export function preferredAgentsError(v: unknown): string | null {
  if (!Array.isArray(v)) return 'Preferred agents must be a list of agent kinds.';
  if (v.length > MAX_PREFERRED_AGENT_KINDS) return `Name at most ${MAX_PREFERRED_AGENT_KINDS} preferred agents.`;
  if (v.some((k) => typeof k !== 'string' || !/^[a-z0-9._-]{1,40}$/.test(k))) return 'That agent kind is not valid.';
  if (new Set(v).size !== v.length) return 'Name each preferred agent once.';
  return null;
}

/** Audit labels for the actions this module records. */
export const SCHEDULER_ACTION_LABEL: Record<string, string> = {
  'queue.add': 'Queue mission',
  'queue.cancel': 'Cancel queued mission',
  'queue.retry': 'Retry queued mission',
  'queue.start': 'Start queued mission',
  'queue.failover': 'Moved to another agent',
  'node.labels': 'Edit machine labels',
  'node.limit': 'Set machine limit',
  'node.drain': 'Drain machine',
  'node.undrain': 'Resume machine',
  'scheduler.settings': 'Edit scheduling settings',
  'scheduler.project': 'Edit project scheduling',
  'rule.add': 'Add time rule',
  'rule.delete': 'Delete time rule',
  'pool.create': 'Create node pool',
  'pool.update': 'Edit node pool',
  'pool.delete': 'Delete node pool',
  'pool.scale-up': 'Add pool machine',
  'pool.scale-down': 'Remove pool machine',
};

// ---------------------------------------------------------------------------
// Labels and limits
// ---------------------------------------------------------------------------

/** A label: lower case letters, digits and . _ : / - ; up to 40 characters. */
export const LABEL_RE = /^[a-z0-9][a-z0-9._:/-]{0,39}$/;

/** Trim, lower-case, drop invalid and duplicate labels. */
export function cleanLabels(list: unknown, max = 20): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  for (const x of list) {
    if (typeof x !== 'string') continue;
    const l = x.trim().toLowerCase();
    if (LABEL_RE.test(l) && !out.includes(l)) out.push(l);
    if (out.length >= max) break;
  }
  return out;
}

/** 'linux, gpu  big' -> ['linux', 'gpu', 'big']. */
export function parseLabels(text: string): string[] {
  return cleanLabels(text.split(/[\s,]+/).filter(Boolean));
}

/** A machine's labels as the scheduler sees them: Orgus's (else the agent's), plus its OS. */
export function effectiveLabels(m: Pick<Machine, 'os' | 'labels'>, node?: Pick<NodeSettings, 'labels'> | null): string[] {
  const base = node?.labels ?? cleanLabels(m.labels);
  return base.includes(m.os) ? base : [...base, m.os];
}

/** Runtimes a machine's labels name (`runtime:<name>`), or Claude Code when they name none. */
export function runtimesOf(labels: readonly string[]): string[] {
  const named = labels.filter((l) => l.startsWith('runtime:')).map((l) => l.slice(8));
  return named.length ? named : [DEFAULT_RUNTIME];
}

/**
 * The agent kinds a machine can start a mission with (ADR 0049 decision 5): its `runtime:<name>`
 * labels when it has any (the owner pins what runs there); else the kinds its agent can start
 * whose CLI is installed (when it reports runtimes); else Claude Code, which an agent that
 * predates adapters starts whatever it is asked.
 */
export function offeredAgentKinds(m: Pick<Machine, 'agentKinds' | 'runtimes'>, labels: readonly string[]): string[] {
  if (labels.some((l) => l.startsWith('runtime:'))) return runtimesOf(labels);
  if (!m.agentKinds?.length) return [DEFAULT_RUNTIME];
  if (!m.runtimes) return [...m.agentKinds];
  const installed = new Set(m.runtimes.filter((r) => r.installed).map((r) => r.kind));
  return m.agentKinds.filter((k) => installed.has(k));
}

/** Kinds whose ACP `initialize` reported, at their last start on the machine, that they are not signed in. */
export function signedOutKinds(m: Pick<Machine, 'runtimes'>): string[] {
  return (m.runtimes ?? []).filter((r) => r.acp?.signedIn === false).map((r) => r.kind);
}

/**
 * The kind an entry starts with on a machine offering `offered`: a pinned kind when offered;
 * for 'auto', the first preferred kind offered and not avoided, then the other offered kinds in
 * the machine's order. No vendor is named here: preference is policy, the rest is reported data.
 */
export function pickAgentKind(needs: Pick<MissionNeeds, 'runtime' | 'avoid' | 'prefer'>, offered: readonly string[], preferred: readonly string[] = []): string | null {
  if (needs.runtime !== AUTO_AGENT) return offered.includes(needs.runtime) ? needs.runtime : null;
  const avoid = new Set(needs.avoid ?? []);
  return [...(needs.prefer ? [needs.prefer] : []), ...preferred, ...offered].find((k) => offered.includes(k) && !avoid.has(k)) ?? null;
}

/**
 * What a project's policy says about agent kinds (ADR 0049 decision 5, PolicySettings
 * allowedAgentKinds, project over org), resolved by the hub's policy module. Which kind to try
 * first is the hub's own setting (SchedulerSettings), not policy. Additive, 19 Oct 2026.
 */
export interface AgentKindPolicy {
  /** Kinds a mission may run; null: every kind. */
  allowed: readonly string[] | null;
  /** Policy needs the agent to ask before it acts (approvals answered by policy, Orgus or a person): 'auto' never picks a kind that cannot ask. */
  needsApprovals: boolean;
}

/**
 * Can this kind on this machine ask for approvals? From what its ACP `initialize` reported when
 * the machine has a summary (the generic ACP row, with the overlay's verified caveat), else the
 * kind's own capability row; a kind only the ACP Registry knows (`acpKinds`) is an ACP agent.
 */
export function approvalsSupportOn(m: Pick<Machine, 'runtimes'>, kind: string, acpKinds: readonly string[] = []): AgentSupport {
  const def = acpAgent(kind);
  const summary = m.runtimes?.find((r) => r.kind === kind)?.acp;
  if (summary) return (def ? acpCapabilities(def, summary) : ACP_BASE_CAPABILITIES).approvals.support;
  if (!def && acpKinds.includes(kind)) return ACP_BASE_CAPABILITIES.approvals.support;
  return agentCapabilities(kind).approvals.support;
}

/**
 * The kinds an entry may start with on a machine under its project's policy: offered kinds the
 * policy allows, and for 'auto' (a person who pins a kind chose it) only kinds that can ask for
 * approvals when policy needs them. `why` says what policy took away, for the wait message.
 */
export function policyOffered(
  m: Pick<Machine, 'runtimes'>,
  offered: readonly string[],
  needs: Pick<MissionNeeds, 'runtime'>,
  policy: AgentKindPolicy | undefined,
  acpKinds: readonly string[] = [],
): { offered: string[]; why: string | null } {
  if (!policy) return { offered: [...offered], why: null };
  const allowed = policy.allowed ? offered.filter((k) => policy.allowed!.includes(k)) : [...offered];
  if (offered.length && !allowed.length) return { offered: [], why: 'offers no agent kind the project policy allows' };
  if (needs.runtime !== AUTO_AGENT && offered.includes(needs.runtime) && !allowed.includes(needs.runtime)) return { offered: allowed, why: `offers ${needs.runtime}, which the project policy does not allow` };
  if (needs.runtime !== AUTO_AGENT || !policy.needsApprovals) return { offered: allowed, why: null };
  const asking = allowed.filter((k) => approvalsSupportOn(m, k, acpKinds) !== 'no');
  return { offered: asking, why: allowed.length && !asking.length ? 'offers only agent kinds that cannot ask for approvals, which the project policy needs' : null };
}

/**
 * The permission mode a mission starts its session with. A headless agent that cannot ask Orgus
 * for approval (approvalsSupportOn 'no', such as Codex) cannot honour "Ask before acting" (or no
 * mode): nobody answers its prompts, so it would run in its own sandbox and end blocked. It gets
 * Accept edits, under which Orgus lets it work directly in its checkout (ADR 0049 decision 9).
 * Any other start keeps the mode it asked for (undefined: the agent's default).
 */
export function missionPermissionMode(asked: string | null | undefined, mode: string, kind: string, m: Pick<Machine, 'runtimes'> | undefined, acpKinds: readonly string[] = []): string | undefined {
  const given = asked ?? undefined;
  if (mode !== 'headless' || (given !== undefined && given !== 'default')) return given;
  return approvalsSupportOn(m ?? {}, kind, acpKinds) === 'no' ? 'acceptEdits' : given;
}

/** Sessions a machine runs at once: its own limit, else CPU count / 2, at least 1. */
export function nodeLimit(m: Pick<Machine, 'cpus'>, node?: Pick<NodeSettings, 'maxSessions'> | null): number {
  if (node?.maxSessions != null && node.maxSessions >= 0) return Math.floor(node.maxSessions);
  return Math.max(1, Math.floor((m.cpus || 0) / 2));
}

// ---------------------------------------------------------------------------
// Admission: does a machine take one more session (ADR 0063 step 5)
// ---------------------------------------------------------------------------

/** What placement reads from a machine's load report (module 'machine-load'). */
export type LoadSignal = Pick<MachineLoadReport, 'load' | 'windowMs' | 'work'>;

/**
 * What one more session is expected to cost in memory. Measured on the owner's machine on
 * 18 Oct 2026: 34 `claude.exe` processes, private working set median 200 MB, mean 250 MB, largest
 * 750 MB; with the MCP servers and hooks a session starts, about half a gigabyte. Agents do not
 * report a session's memory yet, so this is the documented default.
 */
export const SESSION_MEM_BYTES = 512 * 1024 ** 2;
/** Sessions may hold this share of a machine's memory: the sanity cap when no limit is set. */
export const SESSION_MEM_SHARE = 0.5;
/** A work budget with more heavy commands waiting than this takes no new session. */
export const MAX_WORK_WAITERS = 1;
/** Within this much of the CPU ceiling (or of the memory floor) a machine is tight, not roomy. */
export const TIGHT_MARGIN = 0.1;

/**
 * 'measured': a fresh load report with the work budget; 'sessions': none (an older agent, a stale
 * report, an unreadable lease folder), so the earlier rules count sessions instead.
 */
export type AdmissionBasis = 'measured' | 'sessions';
/** room: takes work; tight: takes work, but near a limit; full: no room now; closed: offline, drained or outside its hours. */
export type AdmissionState = 'room' | 'tight' | 'full' | 'closed';
export const ADMISSION_STATE_LABEL: Record<AdmissionState, string> = {
  room: 'Has room',
  tight: 'Nearly full',
  full: 'Full',
  closed: 'Not taking work',
};

export interface Admission {
  basis: AdmissionBasis;
  state: AdmissionState;
  /** One plain line, no machine name: '12 of 18 slots in use, 1 waiting', 'Memory 92% used'. */
  text: string;
  /** Sessions that fit now (the session cap and, when measured, memory); 0 when full or closed. */
  more: number;
  /**
   * Heavy-work headroom in slots, the routing key: the budget's free slots less what waits and
   * less one per start placed but not reported, when measured; free sessions otherwise.
   */
  free: number;
  work: { budget: number; used: number; waiting: number } | null;
  /** CPU busy, 0..1: over the report's window when measured. */
  cpu: number;
  /** Memory in use, 0..1. */
  memUsed: number;
  /** Where the session cap comes from: the owner's limit, memory, or CPU count / 2. */
  limitFrom: 'set' | 'memory' | 'cpus';
}

/** The session cap: the owner's limit, else memory while the load is measured, else CPU count / 2. */
export function sessionCap(m: Pick<Machine, 'cpus' | 'memTotal'>, node: Pick<NodeSettings, 'maxSessions'> | null | undefined, measured: boolean): { limit: number; from: Admission['limitFrom'] } {
  if (node?.maxSessions != null && node.maxSessions >= 0) return { limit: Math.floor(node.maxSessions), from: 'set' };
  if (measured && m.memTotal > 0) return { limit: Math.max(1, Math.floor((m.memTotal * SESSION_MEM_SHARE) / SESSION_MEM_BYTES)), from: 'memory' };
  return { limit: nodeLimit(m), from: 'cpus' };
}

/**
 * Routing order (ADR 0063 step 5): machines with measured room first, then machines judged by the
 * earlier rules, then nearly full, full and closed ones; within each, the most heavy-work headroom, then the most free
 * memory, then the least load, then the name. A machine near its ceiling is never picked while
 * another with room fits.
 */
export function admissionOrder(
  a: { admission: Admission; memFree: number; load: number; name: string },
  b: { admission: Admission; memFree: number; load: number; name: string },
): number {
  const tier = (x: Admission) => (x.state === 'room' ? (x.basis === 'measured' ? 0 : 1) : x.state === 'tight' ? 2 : x.state === 'full' ? 3 : 4);
  return tier(a.admission) - tier(b.admission) || b.admission.free - a.admission.free || b.memFree - a.memFree || a.load - b.load || a.name.localeCompare(b.name);
}

export function projectLimit(settings: SchedulerSettings, projectId: Id): number | null {
  const own = settings.projects[projectId]?.maxSessions;
  return own != null ? own : settings.limits.perProject;
}

export function offlinePolicy(settings: SchedulerSettings, projectId: Id): OfflinePolicy {
  return settings.projects[projectId]?.offline ?? settings.defaultOffline;
}

// ---------------------------------------------------------------------------
// Time rules
// ---------------------------------------------------------------------------

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$|^24:00$/;

export function minutesOf(hhmm: string): number | null {
  if (!HHMM.test(hhmm)) return null;
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

/** Local wall-clock minute of the day and weekday at `now`. */
export function localClock(now: number, utcOffsetMinutes: number | null): { minute: number; day: number } {
  const offset = utcOffsetMinutes ?? -new Date(now).getTimezoneOffset();
  const d = new Date(now + offset * 60_000);
  return { minute: d.getUTCHours() * 60 + d.getUTCMinutes(), day: d.getUTCDay() };
}

/** True when the rule's window is open at `now`. */
export function ruleActive(rule: Pick<TimeRule, 'from' | 'to' | 'days'>, now: number, utcOffsetMinutes: number | null): boolean {
  const from = minutesOf(rule.from);
  const to = minutesOf(rule.to);
  if (from == null || to == null) return false;
  const { minute, day } = localClock(now, utcOffsetMinutes);
  const onDay = (d: number) => !rule.days.length || rule.days.includes(d);
  if (from === to || (from === 0 && to === 1440)) return onDay(day);
  if (from < to) return minute >= from && minute < to && onDay(day);
  // Runs past midnight: the evening part belongs to today, the early part to yesterday's window.
  if (minute >= from) return onDay(day);
  if (minute < to) return onDay((day + 6) % 7);
  return false;
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "No new work on laptop from 22:00 to 07:00, Mon to Fri". */
export function describeRule(rule: Pick<TimeRule, 'label' | 'from' | 'to' | 'days'>): string {
  const when = rule.from === rule.to ? 'all day' : rule.to === '24:00' ? `after ${rule.from}` : `from ${rule.from} to ${rule.to}`;
  const days = [...rule.days].sort((a, b) => a - b);
  let on = '';
  if (days.length && days.length < 7) {
    const run = days.every((d, i) => i === 0 || d === days[i - 1]! + 1);
    on = run && days.length > 2 ? `, ${DAY_SHORT[days[0]!]} to ${DAY_SHORT[days[days.length - 1]!]}` : `, ${days.map((d) => DAY_SHORT[d]).join(' ')}`;
  }
  return `No new work on ${rule.label} ${when}${on}`;
}

export function blockingRule(labels: readonly string[], rules: readonly TimeRule[], now: number, utcOffsetMinutes: number | null): TimeRule | null {
  return rules.find((r) => labels.includes(r.label) && ruleActive(r, now, utcOffsetMinutes)) ?? null;
}

// ---------------------------------------------------------------------------
// The scheduler
// ---------------------------------------------------------------------------

/** Work that occupies a slot: a live session, or a start placed but not reported yet. */
export interface RunningWork {
  machineId: Id;
  projectId: Id | null;
  person: string | null;
  /** A start not reported yet. */
  reserved?: boolean;
}

/** A placed start holds its slot this long while its session has not been reported. */
export const RESERVE_MS = 3 * 60_000;

/**
 * What occupies slots right now: every live session (attributed to a person through `personOf`,
 * the owner of the mission that started it), plus starts placed by the scheduler whose sessions
 * have not appeared yet.
 */
export function runningWork(
  sessions: Iterable<{ id: Id; machineId: Id; projectId: Id | null; status: string }>,
  queue: readonly QueueEntry[],
  personOf: (sessionId: Id) => string | null,
  now: number,
): RunningWork[] {
  const out: RunningWork[] = [];
  const known = new Set<Id>();
  for (const s of sessions) {
    known.add(s.id);
    if (s.status === 'ended') continue;
    out.push({ machineId: s.machineId, projectId: s.projectId, person: personOf(s.id) });
  }
  for (const e of queue) {
    if (!e.placedOn) continue;
    const pending = e.state === 'starting' || (e.state === 'started' && !(e.sessionId && known.has(e.sessionId)) && e.startedAt != null && now - e.startedAt < RESERVE_MS);
    if (pending) out.push({ machineId: e.placedOn, projectId: e.projectId, person: e.person, reserved: true });
  }
  return out;
}

export interface CheckoutRef {
  id: Id;
  projectId: Id;
  machineId: Id;
  kind: 'main' | 'worktree';
  path: string;
}

export interface ScheduleInput {
  now: number;
  machines: readonly Machine[];
  nodes: Readonly<Record<Id, NodeSettings>>;
  settings: SchedulerSettings;
  running: readonly RunningWork[];
  queue: readonly QueueEntry[];
  checkouts: readonly CheckoutRef[];
  /**
   * Machines where a project lands or deploys now (module 'project-protocol'): machine id to
   * "Waits for Qawl: landing 5 submissions". Queued work that is not urgent waits there.
   */
  yields?: Readonly<Record<Id, string>>;
  /** Queue entries that no longer wait for landings (their hold timed out, or a person chose Start now). */
  yieldExempt?: ReadonlySet<Id>;
  /**
   * Computers approved into a person's personal scope (ADR 0042, module 'pairing'): machine id to
   * that person's login. Work someone else queued is never placed there unless pinned to it (a
   * pinned start is judged by the pairing gate, which lets admins through). Additive.
   */
  personal?: Readonly<Record<Id, string>>;
  /**
   * Fresh load reports by machine id (module 'machine-load', younger than MACHINE_LOAD_TTL_MS).
   * A machine with a report that carries its work budget is judged by heavy-work headroom and
   * memory; one without falls back to session count. Additive, 18 Oct 2026.
   */
  loads?: Readonly<Record<Id, LoadSignal>>;
  /** Kinds 'auto' entries try first, in order (policy); the rest follow in the order the machine reports them. */
  preferredAgentKinds?: readonly string[];
  /** Kinds the owner turned off (SchedulerSettings.agents.off): never placed. Additive, 20 Oct 2026. */
  offAgentKinds?: readonly string[];
  /**
   * What each machine's own ceiling refuses, by machine id (CeilingsReport.deny, module 'trust'):
   * a kind it refuses (ceilingRefusesAgent) is never placed there, and a mission pinned to it
   * waits and says how to allow it. Additive, 20 Oct 2026.
   */
  ceilings?: Readonly<Record<Id, readonly string[]>>;
  /** Kinds whose credits the owner saves (SchedulerSettings.agents.spare): placed last unless the work is large. Additive, 20 Oct 2026. */
  spareAgentKinds?: readonly string[];
  /**
   * Agent-kind policy per project id (module policy): allowed kinds, and whether 'auto' needs a
   * kind that can ask for approvals. A project without an entry is not constrained. Additive, 19 Oct 2026.
   */
  agentPolicies?: Readonly<Record<Id, AgentKindPolicy>>;
  /** Kinds only the ACP Registry knows (module acp-registry): ACP agents, which can ask for approvals. */
  acpKinds?: readonly string[];
  /**
   * What finished missions taught (agent-score.ts `outcomeOf`): with it, an 'auto' entry takes the
   * best-scored kind and model instead of the first in order, and its placement says why. Additive, 19 Oct 2026.
   */
  outcomes?: readonly AgentOutcome[];
  /** Work size per queue entry id, from its item's estimate (`workSizeOf`). */
  sizes?: Readonly<Record<Id, WorkSize>>;
  /**
   * Agent kinds cooling down after a failure of their own, by machine (`coolingOf`, module
   * 'agent-health'): an 'auto' entry skips them there, a pinned kind waits. Additive, 19 Oct 2026.
   */
  cooling?: CoolingMap;
  /** Kinds whose runtime breaker stops new work, by machine, with the reason (agent-breaker.ts brokenKindsOf). Additive, ADR 0089. */
  broken?: BrokenMap;
  /** Subscription usage per machine id (module 'agent-usage'): scoring's capacity term. Additive, 20 Oct 2026. */
  usage?: Readonly<Record<Id, readonly AgentUsage[]>>;
  /**
   * Per entry, the cooling it sees instead of `cooling`: the kinds whose account it would run on
   * ran out on a machine and no fallback account can take it there (ADR 0071 decision 9,
   * amended). Additive, 20 Oct 2026.
   */
  coolingByEntry?: Readonly<Record<Id, CoolingMap>>;
  /**
   * A hub restart waits for launched sessions to end (ORGU-154, `orgus follow-main`): admission is
   * closed to everything but urgent work (priority 0, a red-main fix), so the drain empties. Additive, 30 Oct 2026.
   */
  restartPending?: RestartPending | null;
}

/** A hub restart `orgus follow-main` holds until the launched sessions it would end are done (ORGU-154). */
export interface RestartPending {
  /** When the drain began. */
  since: Ts;
  /** The main it restarts onto. */
  sha: string;
  /** Launched sessions it still waits for. */
  waitsFor: number;
}

/** The one line Capacity, the Autopilot bar and a waiting entry show while a hub restart is pending. */
export function restartPendingText(r: Pick<RestartPending, 'waitsFor'>): string {
  const n = r.waitsFor;
  return `Hub restart pending: it waits for ${n} session${n === 1 ? '' : 's'} to end. Only red-main fixes start until then`;
}

export interface Placement {
  entryId: Id;
  machineId: Id;
  checkoutId: Id;
  /** The agent kind the session starts with: the pinned kind, or the one 'auto' chose there. */
  agentKind: string;
  /** 'auto' with scoring: the model to start with (null: the mission's own, or the agent's default). Additive, 19 Oct 2026. */
  model?: string | null;
  /** 'auto' with scoring: one line saying why this kind (and model). Additive, 19 Oct 2026. */
  reason?: string;
}

export interface Waiting {
  entryId: Id;
  reason: WaitReason;
  message: string;
  /** For reason 'project-busy': the first machine it waits on. */
  heldOn?: Id;
}

export interface ScheduleResult {
  placements: Placement[];
  waiting: Waiting[];
  capacity: NodeCapacity[];
}

const REASON_RANK: Record<WaitReason, number> = {
  'no-machine': 0,
  'no-checkout': 1,
  runtime: 2,
  labels: 3,
  'pinned-offline': 4,
  offline: 4,
  drained: 5,
  'time-rule': 6,
  headroom: 7,
  'node-full': 8,
  'work-full': 8,
  'project-limit': 9,
  'person-limit': 9,
  'project-busy': 7,
  paused: 10,
  restart: 11,
};

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

/** Queue order: priority, then first come first served. */
export function queueOrder(a: Pick<QueueEntry, 'priority' | 'enqueuedAt' | 'id'>, b: Pick<QueueEntry, 'priority' | 'enqueuedAt' | 'id'>): number {
  return a.priority - b.priority || a.enqueuedAt - b.enqueuedAt || a.id.localeCompare(b.id);
}

interface NodeEval {
  m: Machine;
  labels: string[];
  limit: number;
  used: number;
  rule: TimeRule | null;
  memFree: number;
  /** Why it takes no new work at all, regardless of the mission. */
  closed: { reason: WaitReason; text: string } | null;
  admission: Admission;
}

function gb(bytes: number): string {
  return `${(Math.max(0, bytes) / 1e9).toFixed(1)} GB`;
}

/** 'HH:MM' on the hub's (or the settings') clock. */
function clockText(at: number, utcOffsetMinutes: number | null): string {
  const { minute } = localClock(at, utcOffsetMinutes);
  return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
}

function secs(ms: number): string {
  return ms >= 90_000 ? `${Math.round(ms / 60_000)} min` : `${Math.round(ms / 1000)} s`;
}

/**
 * One machine as placement sees it now: its labels, session cap, and whether it takes one more
 * session (the admission gates, in order: online, not drained, no time rule, CPU under the
 * ceiling, memory over the floor after one more session, a short work queue).
 */
function evalNode(m: Machine, input: ScheduleInput, used: number, reserved = 0): NodeEval {
  const node = input.nodes[m.id];
  const labels = effectiveLabels(m, node);
  const sig = input.loads?.[m.id];
  const work = sig?.work ?? null;
  const measured = !!work;
  const { limit, from } = sessionCap(m, node, measured);
  const memFree = m.memTotal > 0 ? m.memFree / m.memTotal : 1;
  const memUsed = 1 - memFree;
  const rule = blockingRule(labels, input.settings.rules, input.now, input.settings.utcOffsetMinutes);
  const h = input.settings.headroom;
  // Measured: the report's average over its window, so a short spike does not close the machine.
  const cpu = measured && sig!.windowMs > 0 ? sig!.load : m.load;
  // Memory after the starts placed but not reported yet, and after one more.
  const floorBytes = h.minFreeMemory * m.memTotal;
  const memAfter = m.memFree - (reserved + 1) * SESSION_MEM_BYTES;
  const waiting = work?.waiting.length ?? 0;
  const wanted = work ? work.waiting.reduce((s, w) => s + Math.max(1, w.want), 0) : 0;
  const slotsFree = work ? Math.max(0, work.budget - work.used - wanted) : 0;
  const slotsText = work ? `${work.used} of ${work.budget} slots in use${waiting ? `, ${waiting} waiting` : ''}` : '';
  const cpuText = measured && sig!.windowMs > 0 ? `CPU ${pct(cpu)} over ${secs(sig!.windowMs)}` : `CPU ${pct(cpu)}`;

  let closed: NodeEval['closed'] = null;
  let state: AdmissionState = 'room';
  let text = '';
  if (!m.online) closed = { reason: 'offline', text: `${m.name} is offline` };
  else if (node?.drain) closed = { reason: 'drained', text: `${m.name} is drained` };
  else if (rule) closed = { reason: 'time-rule', text: `${m.name}: ${describeRule(rule).replace(/^No new work/, 'no new work')}` };
  if (closed) {
    state = 'closed';
    text = !m.online ? 'Offline' : node?.drain ? `Drained by ${node.drain.by}` : describeRule(rule!);
  } else if (cpu > h.maxLoad) {
    closed = { reason: 'headroom', text: measured ? `${m.name} is busy (${cpuText}, ceiling ${pct(h.maxLoad)})` : `${m.name} is busy (CPU ${pct(m.load)}, limit ${pct(h.maxLoad)})` };
    state = 'full';
    text = `${cpuText}, ceiling ${pct(h.maxLoad)}`;
  } else if (memFree < h.minFreeMemory) {
    closed = { reason: 'headroom', text: measured ? `${m.name} is low on memory (${pct(memUsed)} used)` : `${m.name} is low on memory (${pct(memFree)} free)` };
    state = 'full';
    text = `Memory ${pct(memUsed)} used`;
  } else if (measured && memAfter < floorBytes) {
    closed = { reason: 'headroom', text: `${m.name} is low on memory (${gb(m.memFree)} free; a session needs about ${gb(SESSION_MEM_BYTES)} above the ${pct(h.minFreeMemory)} floor)` };
    state = 'full';
    text = `Memory ${pct(memUsed)} used, ${gb(m.memFree)} free`;
  } else if (measured && waiting > MAX_WORK_WAITERS) {
    closed = { reason: 'work-full', text: `${m.name} has heavy work queued (${slotsText})` };
    state = 'full';
    text = slotsText;
  } else if (used >= limit) {
    state = 'full';
    text = from === 'set' ? `${used} of ${limit} sessions, its limit` : from === 'memory' ? `${used} of ${limit} sessions, the cap its memory allows` : `${used} of ${limit} sessions`;
  } else {
    const nearCpu = cpu >= h.maxLoad - TIGHT_MARGIN;
    const nearMem = measured ? memAfter < floorBytes + TIGHT_MARGIN * m.memTotal * 0.5 : memFree < h.minFreeMemory + TIGHT_MARGIN * 0.5;
    const nearWork = measured && (slotsFree - reserved <= 0 || waiting > 0);
    state = nearCpu || nearMem || nearWork ? 'tight' : 'room';
    if (measured) text = nearCpu && !nearWork ? `${cpuText}, ceiling ${pct(h.maxLoad)}` : nearMem && !nearWork ? `Memory ${pct(memUsed)} used, ${gb(m.memFree)} free` : `${slotsText}, memory ${pct(memUsed)} used`;
    else text = `${used} of ${limit} sessions, ${nearCpu ? `${cpuText}, ` : ''}no load report`;
  }
  if (state === 'full' && !measured && (closed?.reason === 'headroom' || used >= limit)) text = `${text}; no load report`;

  const memRoom = Math.floor((m.memFree - reserved * SESSION_MEM_BYTES - floorBytes) / SESSION_MEM_BYTES);
  const more = state === 'room' || state === 'tight' ? Math.max(0, Math.min(limit - used, measured ? memRoom : limit - used)) : 0;
  const admission: Admission = {
    basis: measured ? 'measured' : 'sessions',
    state,
    text,
    more,
    free: measured ? Math.max(0, slotsFree - reserved) : Math.max(0, limit - used),
    work: work ? { budget: work.budget, used: work.used, waiting } : null,
    cpu,
    memUsed,
    limitFrom: from,
  };
  return { m, labels, limit, used, rule, memFree, closed, admission };
}

/**
 * Place queued missions on machines. Pure: the same input always gives the same answer.
 *
 *   order       priority, then first come first served; a mission that cannot run does not hold
 *               back the ones behind it (they may still fit elsewhere)
 *   eligible    has a checkout of the project, offers the runtime, carries every needed label,
 *               online, not drained, no time rule open, CPU under the ceiling, memory over the
 *               floor (after one more session, when measured), a short work queue (measured),
 *               under its session cap
 *   limits      per machine (the owner's, else from memory when measured, else CPU count / 2),
 *               per project and per person
 *   pinned      only its machine; when that is offline the project's offline policy decides
 *               whether it waits or runs elsewhere
 *   choice      `admissionOrder`: measured room first, then the most heavy-work headroom, the
 *               most free memory, the least load, the name
 *
 * Each mission left waiting gets the reason that came closest to placing it, in a plain sentence.
 */
export function schedule(input: ScheduleInput): ScheduleResult {
  const usedOn = new Map<Id, number>();
  const reservedOn = new Map<Id, number>();
  const usedByProject = new Map<Id, number>();
  const usedByPerson = new Map<string, number>();
  const usage = new Map<Id, Map<string, { person: string | null; projectId: Id | null; count: number }>>();
  const bump = <K>(map: Map<K, number>, k: K, n = 1) => map.set(k, (map.get(k) ?? 0) + n);
  const addUsage = (w: RunningWork) => {
    bump(usedOn, w.machineId);
    if (w.reserved) bump(reservedOn, w.machineId);
    if (w.projectId) bump(usedByProject, w.projectId);
    if (w.person) bump(usedByPerson, w.person);
    let u = usage.get(w.machineId);
    if (!u) usage.set(w.machineId, (u = new Map()));
    const key = `${w.person ?? ''}\u0000${w.projectId ?? ''}`;
    const row = u.get(key) ?? { person: w.person, projectId: w.projectId, count: 0 };
    row.count++;
    u.set(key, row);
  };
  for (const w of input.running) addUsage(w);

  const machines = [...input.machines].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  const checkoutsOf = new Map<string, CheckoutRef[]>();
  for (const c of input.checkouts) {
    const k = `${c.projectId}\u0000${c.machineId}`;
    const list = checkoutsOf.get(k) ?? [];
    list.push(c);
    checkoutsOf.set(k, list);
  }
  const pickCheckout = (projectId: Id, machineId: Id): CheckoutRef | null => {
    const list = checkoutsOf.get(`${projectId}\u0000${machineId}`) ?? [];
    return [...list].sort((a, b) => Number(a.kind === 'worktree') - Number(b.kind === 'worktree') || a.path.localeCompare(b.path))[0] ?? null;
  };

  const placements: Placement[] = [];
  const waiting: Waiting[] = [];
  const queued = input.queue.filter((e) => e.state === 'queued').sort(queueOrder);

  for (const e of queued) {
    // ORGU-154: a pending hub restart closes admission to all but urgent work (a red-main fix).
    if (input.restartPending && e.priority > 0) {
      waiting.push({ entryId: e.id, reason: 'restart', message: `${restartPendingText(input.restartPending)}.` });
      continue;
    }
    const pLimit = projectLimit(input.settings, e.projectId);
    const pUsed = usedByProject.get(e.projectId) ?? 0;
    if (pLimit != null && pUsed >= pLimit) {
      waiting.push({ entryId: e.id, reason: 'project-limit', message: `The project already runs ${plural(pUsed, 'session')}, its limit. It starts when one finishes.` });
      continue;
    }
    const perPerson = input.settings.limits.perPerson;
    const mine = usedByPerson.get(e.person) ?? 0;
    if (perPerson != null && mine >= perPerson) {
      waiting.push({ entryId: e.id, reason: 'person-limit', message: `${e.person} already runs ${plural(mine, 'session')}, the limit per person. It starts when one finishes.` });
      continue;
    }
    if (!machines.length) {
      waiting.push({ entryId: e.id, reason: 'no-machine', message: 'No machine is connected to this hub. Add one in Machines.' });
      continue;
    }

    let pool = machines;
    if (e.machineId) {
      const pinned = machines.find((m) => m.id === e.machineId);
      if (!pinned) {
        waiting.push({ entryId: e.id, reason: 'no-machine', message: 'The machine it was pinned to was removed. Cancel it and queue it again.' });
        continue;
      }
      if (!pinned.online && offlinePolicy(input.settings, e.projectId) === 'reschedule') pool = machines;
      else if (!pinned.online) {
        waiting.push({ entryId: e.id, reason: 'pinned-offline', message: `Waiting for ${pinned.name} to come back online. The project waits for its machine; set it to run elsewhere to move it.` });
        continue;
      } else pool = [pinned];
    }

    let best: { n: NodeEval; checkout: CheckoutRef; agentKind: string; pick: AgentPick | null } | null = null;
    let closest: { reason: WaitReason; texts: string[] } | null = null;
    let heldOn: Id | undefined;
    /** A kind it could run was cooling down somewhere (the runtime wait then says so). */
    let cooled = false;
    /** A kind it could run has an open runtime breaker somewhere (ADR 0089). */
    let broke = false;
    /** A kind it could run reported that it is not signed in somewhere. */
    let signedOut = false;
    /** The kind it is pinned to is turned off. */
    let turnedOff = false;
    /** The kind it is pinned to is refused by a machine's ceiling: the message that says how to allow it. */
    let refusedPinned: string | null = null;
    const yields = e.priority > 0 && !input.yieldExempt?.has(e.id) ? input.yields : undefined;
    const miss = (reason: WaitReason, text: string) => {
      if (!closest || REASON_RANK[reason] > REASON_RANK[closest.reason]) closest = { reason, texts: [text] };
      else if (REASON_RANK[reason] === REASON_RANK[closest.reason] && closest.texts.length < 3) closest.texts.push(text);
    };
    for (const m of pool) {
      const owner = input.personal?.[m.id];
      if (owner !== undefined && owner !== (e.for ?? e.by) && !e.machineId) {
        miss('no-machine', `${m.name} is ${owner}'s personal computer`);
        continue;
      }
      const chosen = e.machineId === m.id && e.start.checkoutId ? input.checkouts.find((c) => c.id === e.start.checkoutId && c.machineId === m.id && c.projectId === e.projectId) : undefined;
      const checkout = chosen ?? pickCheckout(e.projectId, m.id);
      if (!checkout) {
        miss('no-checkout', `${m.name} has no checkout of the project`);
        continue;
      }
      const n = evalNode(m, input, usedOn.get(m.id) ?? 0, reservedOn.get(m.id) ?? 0);
      // ADR 0049 decision 5: the project's policy keeps the kinds it allows, and kinds that can ask when it needs approvals.
      // An ACP agent never runs in a mode that skips approvals, so such a start never goes to one.
      const fits = offeredAgentKinds(m, n.labels).filter((k) => acceptsPermissionMode(k, e.start.permissionMode));
      // A kind the owner turned off is never placed: 'auto' skips it, a pinned kind waits and says so.
      const offHere = fits.filter((k) => input.offAgentKinds?.includes(k));
      if (e.needs.runtime !== AUTO_AGENT && offHere.includes(e.needs.runtime)) {
        turnedOff = true;
        miss('runtime', agentOffMessage(e.needs.runtime));
        continue;
      }
      // A kind this machine's own ceiling refuses is off here: 'auto' skips it, a pinned kind waits and says how to allow it.
      const deny = input.ceilings?.[m.id];
      const refusedHere = fits.filter((k) => !offHere.includes(k) && ceilingRefusesAgent(deny, k, e.start.mode) !== null);
      if (e.needs.runtime !== AUTO_AGENT && refusedHere.includes(e.needs.runtime)) {
        refusedPinned ??= agentRefusedMessage(e.needs.runtime, m.name, ceilingRefusesAgent(deny, e.needs.runtime, e.start.mode)!);
        miss('runtime', `${m.name} refuses ${agentKindLabel(e.needs.runtime)} (its ceiling)`);
        continue;
      }
      const allowedHere = policyOffered(m, fits.filter((k) => !offHere.includes(k) && !refusedHere.includes(k)), e.needs, input.agentPolicies?.[e.projectId], input.acpKinds);
      if (allowedHere.why && !allowedHere.offered.includes(e.needs.runtime) && (e.needs.runtime !== AUTO_AGENT || !allowedHere.offered.length)) {
        miss('runtime', `${m.name} ${allowedHere.why}`);
        continue;
      }
      const offered = allowedHere.offered;
      const cooling = (input.coolingByEntry?.[e.id] ?? input.cooling)?.[m.id] ?? {};
      const coolingHere = offered.filter((k) => cooling[k] != null && cooling[k]! > input.now);
      // An ACP agent whose last `initialize` said it is not signed in here (ADR 0049 decision 5).
      const signedOutHere = offered.filter((k) => !coolingHere.includes(k) && signedOutKinds(m).includes(k));
      // ADR 0089: a kind whose runtime breaker is open here waits for its self-test, whatever the clock says.
      const brokenAt = input.broken?.[m.id] ?? {};
      const brokenHere = offered.filter((k) => brokenAt[k] != null && !coolingHere.includes(k) && !signedOutHere.includes(k));
      const blocked = [...coolingHere, ...signedOutHere, ...brokenHere];
      const auto = e.needs.runtime === AUTO_AGENT;
      // A kind cooling down after a failure of its own, or not signed in: 'auto' skips it here, a pinned kind waits for it.
      const agentKind = pickAgentKind(auto ? { ...e.needs, avoid: [...(e.needs.avoid ?? []), ...blocked] } : e.needs, offered, auto ? spareOrder(input.preferredAgentKinds ?? [], offered, input.spareAgentKinds, input.sizes?.[e.id] ?? null) : input.preferredAgentKinds);
      const pinnedBlocked = !auto && agentKind && blocked.includes(agentKind) ? agentKind : null;
      if (!agentKind || pinnedBlocked) {
        if (pinnedBlocked ? coolingHere.includes(pinnedBlocked) : auto && coolingHere.length) cooled = true;
        if (pinnedBlocked ? signedOutHere.includes(pinnedBlocked) : auto && signedOutHere.length) signedOut = true;
        if (pinnedBlocked ? brokenHere.includes(pinnedBlocked) : auto && brokenHere.length) broke = true;
        const why = (k: string) =>
          coolingHere.includes(k)
            ? `${agentKindLabel(k)} cools down after a failure until ${clockText(cooling[k]!, input.settings.utcOffsetMinutes)}`
            : brokenHere.includes(k)
              ? `${agentKindLabel(k)} is broken there (${brokenAt[k]}) until a self-test passes`
              : `${agentKindLabel(k)} needs a sign-in on ${m.name}`;
        const avoided = (e.needs.avoid ?? []).filter((k) => offered.includes(k) && !blocked.includes(k)).map((k) => agentKindLabel(k));
        miss(
          'runtime',
          pinnedBlocked
            ? coolingHere.includes(pinnedBlocked)
              ? `${m.name}: ${why(pinnedBlocked)}`
              : why(pinnedBlocked)
            : auto
              ? `${m.name} has no coding agent it can start${avoided.length ? ` other than ${avoided.join(', ')}` : ''}${blocked.length ? `${avoided.length ? ';' : ','} ${blocked.map(why).join(', ')}` : ''}${offHere.length ? `; ${offHere.map((k) => agentKindLabel(k)).join(', ')} turned off` : ''}${refusedHere.length ? `; ${refusedHere.map((k) => agentKindLabel(k)).join(', ')} refused by its ceiling` : ''}`
              : `${m.name} does not offer ${e.needs.runtime}`,
        );
        continue;
      }
      const missing = e.needs.labels.filter((l) => !n.labels.includes(l));
      if (missing.length) {
        miss('labels', `${m.name} lacks ${missing.join(', ')}`);
        continue;
      }
      if (n.closed) {
        miss(n.closed.reason, n.closed.text);
        continue;
      }
      const busy = yields?.[m.id];
      if (busy) {
        miss('project-busy', `${m.name} ${busy.charAt(0).toLowerCase()}${busy.slice(1)}`);
        heldOn ??= m.id;
        continue;
      }
      if (e.needs.minFreeMemGb != null && m.memFree < e.needs.minFreeMemGb * 1e9) {
        miss('headroom', `${m.name} has ${(m.memFree / 1e9).toFixed(1)} GB free, under the ${e.needs.minFreeMemGb} GB it needs`);
        continue;
      }
      if (n.used >= n.limit) {
        miss('node-full', `${m.name} is full (${n.used} of ${n.limit})`);
        continue;
      }
      const rank = (x: NodeEval) => ({ admission: x.admission, memFree: x.m.memFree, load: x.admission.cpu, name: x.m.name });
      if (!best || admissionOrder(rank(n), rank(best.n)) < 0) {
        // ADR 0049 decision 5, step 3: an 'auto' entry takes the best-scored kind (and model) here among what it may run.
        const pick = auto && input.outcomes ? (scoreAgents({ machine: m, offered: offered.filter((k) => !blocked.includes(k) && !(e.needs.avoid ?? []).includes(k)), projectId: e.projectId, preferred: input.preferredAgentKinds ?? [], outcomes: input.outcomes, requestedModel: e.start.model, size: input.sizes?.[e.id] ?? null, acpKinds: input.acpKinds, spare: input.spareAgentKinds, usage: input.usage?.[m.id], now: input.now, utcOffsetMinutes: input.settings.utcOffsetMinutes, mode: e.start.mode, draw: exploreDraw(e.id) })[0] ?? null) : null;
        best = { n, checkout, agentKind: pick?.kind ?? agentKind, pick };
      }
    }

    if (best) {
      placements.push({ entryId: e.id, machineId: best.n.m.id, checkoutId: best.checkout.id, agentKind: best.agentKind, ...(best.pick ? { model: best.pick.model, reason: best.pick.reason } : {}) });
      addUsage({ machineId: best.n.m.id, projectId: e.projectId, person: e.person, reserved: true });
      continue;
    }
    const c = closest as { reason: WaitReason; texts: string[] } | null;
    const reason = c?.reason ?? 'no-machine';
    const what: Record<WaitReason, string> = {
      'no-machine': 'No machine can run it',
      'no-checkout': 'No machine has a checkout of this project. Add the project on a machine',
      runtime: turnedOff
        ? agentOffMessage(e.needs.runtime).replace(/\.$/, '')
        : signedOut
        ? e.needs.runtime === AUTO_AGENT
          ? cooled
            ? 'Every coding agent that fits needs a sign-in or cools down after a failure. It starts when one is ready'
            : 'Every coding agent that fits needs a sign-in. Sign in on the machine and it starts'
          : `${agentKindLabel(e.needs.runtime)} needs a sign-in. Sign in on the machine, or queue it with another agent`
        : broke && !cooled
        ? e.needs.runtime === AUTO_AGENT
          ? 'Every coding agent that fits is broken on its machine. It starts when one passes its self-test'
          : `${agentKindLabel(e.needs.runtime)} is broken on its machine. It starts once its self-test passes, or queue it with another agent`
        : cooled
        ? e.needs.runtime === AUTO_AGENT
          ? 'Every coding agent that fits failed recently and cools down. It starts when one is healthy again'
          : `${agentKindLabel(e.needs.runtime)} failed recently and cools down. It starts after that, or queue it with another agent`
        : e.needs.runtime === AUTO_AGENT
          ? 'No machine has a coding agent it can start. Install a coding agent on one'
          : `No machine offers ${e.needs.runtime}`,
      labels: `No machine carries ${e.needs.labels.join(', ')}. Label a machine in Machines`,
      'pinned-offline': 'Its machine is offline',
      offline: 'Every machine that fits is offline',
      drained: 'Every machine that fits is drained',
      'time-rule': 'Every machine that fits is outside its hours',
      headroom: 'Every machine that fits is busy',
      'node-full': 'Every machine that fits is full. It starts when a session finishes',
      'work-full': 'Every machine that fits has heavy work queued. It starts when the queue there is short again',
      'project-limit': 'The project is at its limit',
      'person-limit': 'The person is at their limit',
      'project-busy': 'Every machine that fits runs a landing or deploy first, for at most 30 minutes',
      paused: 'Missions are paused',
      restart: 'The hub restarts first',
    };
    // A pinned kind the machine's ceiling refuses: say how to allow it there, in one sentence.
    const message = reason === 'runtime' && refusedPinned && !turnedOff ? refusedPinned : `${what[reason]}${c?.texts.length ? `: ${c.texts.join('; ')}.` : '.'}`;
    waiting.push({ entryId: e.id, reason, message, ...(reason === 'project-busy' && heldOn ? { heldOn } : {}) });
  }

  const capacity: NodeCapacity[] = machines.map((m) => {
    const n = evalNode(m, input, usedOn.get(m.id) ?? 0, reservedOn.get(m.id) ?? 0);
    const node = input.nodes[m.id];
    return {
      machineId: m.id,
      name: m.name,
      online: m.online,
      labels: n.labels,
      labelsSet: node?.labels != null,
      limit: n.limit,
      used: n.used,
      reserved: reservedOn.get(m.id) ?? 0,
      load: m.load,
      memFreeFraction: n.memFree,
      drained: node?.drain ?? null,
      blockedBy: n.rule,
      accepting: !n.closed,
      notAccepting: n.closed ? n.closed.text : null,
      usage: [...(usage.get(m.id)?.values() ?? [])].sort((a, b) => b.count - a.count),
      admission: n.admission,
    };
  });

  return { placements, waiting, capacity };
}

/** One machine's answer to "can I start one more session here". */
export interface RoomVerdict {
  machineId: Id;
  name: string;
  online: boolean;
  /** It has a checkout of the project asked about (true when no project was given). */
  hasCheckout: boolean;
  admission: Admission;
}

/**
 * Where one more session has room now, best first (`admissionOrder`), for the New session dialog,
 * the API's start and the assistant. With a project, machines without a checkout of it come last
 * and say so. Pure: the same `schedule` capacity the hub places by.
 */
export function roomFor(input: ScheduleInput, projectId?: Id | null): RoomVerdict[] {
  const { capacity } = schedule({ ...input, queue: [] });
  const byId = new Map(input.machines.map((m) => [m.id, m]));
  const has = new Set(projectId ? input.checkouts.filter((c) => c.projectId === projectId).map((c) => c.machineId) : input.machines.map((m) => m.id));
  return capacity
    .filter((c) => c.admission)
    .map((c) => ({ machineId: c.machineId, name: c.name, online: c.online, hasCheckout: has.has(c.machineId), admission: c.admission! }))
    .sort((a, b) => {
      const ma = byId.get(a.machineId)!;
      const mb = byId.get(b.machineId)!;
      return Number(b.hasCheckout) - Number(a.hasCheckout) || admissionOrder({ admission: a.admission, memFree: ma.memFree, load: a.admission.cpu, name: a.name }, { admission: b.admission, memFree: mb.memFree, load: b.admission.cpu, name: b.name });
    });
}

/** True when the machine takes one more session now (room or tight). */
export function hasRoom(a: Pick<Admission, 'state'> | null | undefined): boolean {
  return a?.state === 'room' || a?.state === 'tight';
}

// ---------------------------------------------------------------------------
// Node pools: the planner
// ---------------------------------------------------------------------------

/** Waiting reasons more machines could fix. Limits, pins and missing checkouts are not. */
export const SCALABLE_REASONS: readonly WaitReason[] = ['no-machine', 'labels', 'offline', 'drained', 'time-rule', 'headroom', 'node-full', 'work-full'];

export interface PoolPlanInput {
  now: number;
  pools: readonly NodePool[];
  instances: readonly PoolInstance[];
  /** Queued entries with their last waiting reason. */
  queue: readonly Pick<QueueEntry, 'id' | 'state' | 'reason' | 'needs' | 'machineId' | 'unfitPools'>[];
  /** Running work per machine id (a pool machine with work is never removed). */
  busy: ReadonlySet<Id>;
  /**
   * Free slots per machine that takes new work right now (after this pass's placements), from
   * `schedule(...).capacity`. Without it a joined machine counts all its slots when idle, none when busy.
   */
  free?: ReadonlyMap<Id, number>;
}

export interface PoolPlan {
  create: { poolId: Id; count: number; why: string }[];
  destroy: { instanceId: Id; why: string }[];
  /**
   * Waiting missions a joined pool machine with room could not take: more of the same machines
   * would not help them. The hub records these on the entry (`unfitPools`).
   */
  unfit: { entryId: Id; poolId: Id }[];
}

export function liveInstance(i: Pick<PoolInstance, 'state'>): boolean {
  return i.state === 'provisioning' || i.state === 'running';
}

/**
 * Scale pools: up to `min` always; up for missions waiting on capacity that the pool's labels
 * satisfy, counting slots already on their way; down for machines idle past the pool's idle
 * timeout while above `min`, never one with running work. Never above `max`. Each waiting
 * mission is counted for the first pool that fits it, so two pools do not both grow for it.
 *
 * A mission still waiting while one of the pool's joined machines has a free slot could not use
 * that machine (the scheduler tries every machine): it is not demand for the pool, now or later
 * (`unfit`), so a pool never grows to its max for missions its machines cannot run, and its idle
 * machines are removed.
 */
export function planPools(input: PoolPlanInput): PoolPlan {
  const plan: PoolPlan = { create: [], destroy: [], unfit: [] };
  const demand = input.queue.filter((e) => e.state === 'queued' && !e.machineId && e.reason && SCALABLE_REASONS.includes(e.reason));
  const claimed = new Set<Id>();
  const pools = [...input.pools].sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id));
  for (const p of pools) {
    const mine = input.instances.filter((i) => i.poolId === p.id);
    const live = mine.filter(liveInstance);
    const max = Math.max(p.min, p.max);
    let want = 0;
    let why = '';
    if (live.length < p.min) {
      want = p.min - live.length;
      why = `Keeps ${p.min} running (its minimum)`;
    }
    const candidates = demand.filter((e) => !claimed.has(e.id) && !e.unfitPools?.includes(p.id) && e.needs.labels.every((l) => p.labels.includes(l)));
    // Only machines made as the pool is now say anything about what new ones could run.
    const joined = live.filter((i) => i.state === 'running' && !!i.machineId && i.createdAt >= (p.changedAt ?? 0));
    const freeOn = (i: PoolInstance) => (input.free ? (input.free.get(i.machineId!) ?? 0) : input.busy.has(i.machineId!) ? 0 : Math.max(1, i.slots));
    const roomJoined = joined.reduce((s, i) => s + freeOn(i), 0);
    let fits = candidates;
    if (roomJoined > 0) {
      // A joined machine has room and they still wait: they cannot use this pool's machines.
      for (const e of candidates) plan.unfit.push({ entryId: e.id, poolId: p.id });
      fits = [];
    }
    for (const e of fits) claimed.add(e.id);
    // Slots on their way: machines still provisioning, or running but not joined yet.
    const pending = live.filter((i) => i.state === 'provisioning' || !i.machineId).reduce((s, i) => s + Math.max(1, i.slots), 0);
    const slotsEach = Math.max(1, live[0]?.slots ?? mine[0]?.slots ?? 1);
    const short = fits.length - pending;
    if (short > 0) {
      const extra = Math.ceil(short / slotsEach);
      if (extra > want) {
        want = extra;
        why = `${plural(fits.length, 'mission')} waiting for capacity`;
      }
    }
    want = Math.min(want, Math.max(0, max - live.length));
    if (want > 0) plan.create.push({ poolId: p.id, count: want, why });

    const busy = (i: PoolInstance) => !!i.machineId && input.busy.has(i.machineId);
    const removed = new Set<Id>();
    if (want === 0 && !fits.length) {
      const idle = live
        .filter((i) => i.state === 'running' && !busy(i) && i.idleSince != null && input.now - i.idleSince >= p.idleTimeoutMin * 60_000)
        .sort((a, b) => (a.idleSince ?? 0) - (b.idleSince ?? 0));
      for (const i of idle) {
        if (live.length - removed.size <= p.min) break;
        plan.destroy.push({ instanceId: i.id, why: `Idle for ${p.idleTimeoutMin} min` });
        removed.add(i.id);
      }
    }
    // The maximum was lowered below what runs: remove machines without work, newest first.
    const excess = live.length - removed.size - max;
    if (excess > 0) {
      const over = live
        .filter((i) => !removed.has(i.id) && !busy(i))
        .sort((a, b) => Number(b.state === 'provisioning') - Number(a.state === 'provisioning') || b.createdAt - a.createdAt)
        .slice(0, excess);
      for (const i of over) plan.destroy.push({ instanceId: i.id, why: `Above the pool maximum of ${max}` });
    }
  }
  return plan;
}

// ---------------------------------------------------------------------------
// Validation (shared by the hub and the console)
// ---------------------------------------------------------------------------

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isInt = (v: unknown, lo: number, hi: number) => typeof v === 'number' && Number.isInteger(v) && v >= lo && v <= hi;

export function enqueueError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the request as a JSON object.';
  if (typeof v.missionId !== 'string' || !v.missionId) return 'Choose the mission to queue.';
  if (v.machineId !== undefined && v.machineId !== null && typeof v.machineId !== 'string') return 'The machine must be a machine id.';
  if (v.checkoutId !== undefined && v.checkoutId !== null) {
    if (typeof v.checkoutId !== 'string' || !v.checkoutId) return 'The checkout must be a checkout id.';
    if (!v.machineId) return 'A checkout needs its machine: pin the machine too.';
  }
  if (v.needs !== undefined) {
    if (!isObj(v.needs)) return 'Needs must be an object with labels.';
    if (v.needs.labels !== undefined && (!Array.isArray(v.needs.labels) || v.needs.labels.some((l) => typeof l !== 'string' || !LABEL_RE.test(l.trim().toLowerCase())))) {
      return 'Labels are short words: letters, digits, dot, dash, colon or slash.';
    }
    if (v.needs.runtime !== undefined && (typeof v.needs.runtime !== 'string' || !/^[a-z0-9._-]{1,40}$/.test(v.needs.runtime))) return 'That runtime name is not valid.';
    if (v.needs.minFreeMemGb !== undefined && v.needs.minFreeMemGb !== null && (typeof v.needs.minFreeMemGb !== 'number' || !(v.needs.minFreeMemGb > 0) || v.needs.minFreeMemGb > 4096)) {
      return 'Free memory must be a positive number of GB.';
    }
  }
  if (v.mode !== undefined && v.mode !== 'headless' && v.mode !== 'terminal') return 'The mode must be headless or terminal.';
  if (v.permissionMode !== undefined && v.permissionMode !== null && !(SESSION_PERMISSION_MODES as readonly unknown[]).includes(v.permissionMode)) {
    return `The permission mode must be one of: ${SESSION_PERMISSION_MODES.join(', ')}.`;
  }
  if (v.model !== undefined && v.model !== null && (typeof v.model !== 'string' || !/^[\w.:/@-]{1,100}$/.test(v.model))) return 'That model name is not valid.';
  if (v.newWorktree !== undefined && v.newWorktree !== null && (!isObj(v.newWorktree) || typeof v.newWorktree.branch !== 'string' || !v.newWorktree.branch.trim())) {
    return 'A new worktree needs a branch name.';
  }
  if (v.note !== undefined && v.note !== null && (typeof v.note !== 'string' || v.note.length > 4000)) return 'Keep the note under 4000 characters.';
  if (v.priority !== undefined && !isInt(v.priority, 0, 3)) return 'Priority must be 0 (urgent) to 3 (low).';
  return null;
}

export function manualStartError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the start as a JSON object.';
  if (typeof v.projectId !== 'string' || !v.projectId) return 'Choose the project to start in.';
  if (typeof v.prompt !== 'string' || !v.prompt.trim()) return 'Say what the session should do.';
  if (v.prompt.length > 20_000) return 'Keep the prompt under 20000 characters.';
  if (!(START_WHEN as readonly unknown[]).includes(v.when)) return "Say when to start: 'now', or 'room' to wait until a machine has room.";
  if (v.machineId !== undefined && v.machineId !== null && (typeof v.machineId !== 'string' || !v.machineId)) return 'The machine must be a machine id.';
  if (v.checkoutId !== undefined && v.checkoutId !== null && (typeof v.checkoutId !== 'string' || !v.checkoutId)) return 'The checkout must be a checkout id.';
  if (v.when === 'now' && !v.checkoutId) return 'Starting now needs the checkout to run in.';
  if (v.when === 'room' && v.checkoutId && !v.machineId) return 'A checkout needs its machine: pin the machine too, or leave both out.';
  if (v.mode !== undefined && v.mode !== 'headless' && v.mode !== 'terminal') return 'The mode must be headless or terminal.';
  if (v.permissionMode !== undefined && v.permissionMode !== null && !(SESSION_PERMISSION_MODES as readonly unknown[]).includes(v.permissionMode)) {
    return `The permission mode must be one of: ${SESSION_PERMISSION_MODES.join(', ')}.`;
  }
  if (v.model !== undefined && v.model !== null && (typeof v.model !== 'string' || !/^[\w.:/@-]{1,100}$/.test(v.model))) return 'That model name is not valid.';
  if (v.newWorktree !== undefined && v.newWorktree !== null && (!isObj(v.newWorktree) || typeof v.newWorktree.branch !== 'string' || !v.newWorktree.branch.trim())) {
    return 'A new worktree needs a branch name.';
  }
  if (v.agentKind !== undefined && (typeof v.agentKind !== 'string' || !/^[a-z0-9._-]{1,40}$/.test(v.agentKind))) return 'That agent kind is not valid.';
  if (v.title !== undefined && (typeof v.title !== 'string' || v.title.length > 200)) return 'Keep the title under 200 characters.';
  return null;
}

export function timeRuleError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the rule as a JSON object.';
  if (typeof v.label !== 'string' || !LABEL_RE.test(v.label.trim().toLowerCase())) return 'Name the machine label the rule applies to, for example laptop.';
  if (typeof v.from !== 'string' || minutesOf(v.from) == null || v.from === '24:00') return 'The start time must be HH:MM, for example 22:00.';
  if (typeof v.to !== 'string' || minutesOf(v.to) == null) return 'The end time must be HH:MM, for example 07:00.';
  if (v.days !== undefined && (!Array.isArray(v.days) || v.days.some((d) => !isInt(d, 0, 6)))) return 'Days are numbers from 0 (Sunday) to 6 (Saturday).';
  if (v.note !== undefined && (typeof v.note !== 'string' || v.note.length > 200)) return 'Keep the note under 200 characters.';
  return null;
}

/** Pool names prefix their machines' host names. */
export const POOL_NAME_RE = /^[a-z0-9][a-z0-9-]{0,39}$/i;

export function poolInputError(v: unknown, providers: readonly ProviderInfo[], partial = false): string | null {
  if (!isObj(v)) return 'Send the pool as a JSON object.';
  if (!partial || 'name' in v) {
    if (typeof v.name !== 'string' || !POOL_NAME_RE.test(v.name.trim())) return 'Pool names become machine names: letters, digits and dashes, starting with a letter or digit, up to 40 characters.';
  }
  const provider = providers.find((p) => p.id === v.provider);
  if (!partial || 'provider' in v) {
    if (!provider) return `The provider must be one of: ${providers.map((p) => p.id).join(', ') || 'none configured'}.`;
  }
  if (!partial || 'image' in v) {
    if (typeof v.image !== 'string' || !v.image.trim() || v.image.length > 200) return 'Name the image the machines boot from.';
  }
  if (!partial || 'size' in v) {
    if (typeof v.size !== 'string' || (provider && !provider.sizes.some((s) => s.id === v.size))) {
      return provider ? `The size must be one of: ${provider.sizes.map((s) => s.id).join(', ')}.` : 'Choose a size.';
    }
  }
  if (v.labels !== undefined && (!Array.isArray(v.labels) || v.labels.some((l) => typeof l !== 'string' || !LABEL_RE.test(l.trim().toLowerCase())))) {
    return 'Labels are short words: letters, digits, dot, dash, colon or slash.';
  }
  if (v.min !== undefined && !isInt(v.min, 0, 100)) return 'The minimum must be a whole number from 0 to 100.';
  if (v.max !== undefined && !isInt(v.max, 0, 100)) return 'The maximum must be a whole number from 0 to 100.';
  if (typeof v.min === 'number' && typeof v.max === 'number' && v.min > v.max) return 'The minimum cannot be above the maximum.';
  if (v.idleTimeoutMin !== undefined && !isInt(v.idleTimeoutMin, 1, 7 * 24 * 60)) return 'The idle timeout must be 1 minute to 7 days.';
  return null;
}

export function limitError(v: unknown, what: string): string | null {
  if (v === null || v === undefined) return null;
  if (!isInt(v, 0, 1000)) return `${what} must be a whole number from 0 to 1000, or empty for the default.`;
  return null;
}
